import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ReferenceLine,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';

export const ProfitMarginOverTimeChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance } = useBoardChartData();

    const chartData = useMemo(() => {
        if (!finance?.dailyBreakdown) return [];
        return Object.entries(finance.dailyBreakdown)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, day]: [string, Record<string, unknown>]) => {
                const metrics = day.metrics as Record<string, number> | undefined;
                const revenue = metrics?.revenue ?? 0;
                const expenses = metrics?.expenses ?? 0;
                const margin = revenue !== 0 ? ((revenue - expenses) / revenue) * 100 : 0;
                return { date, margin: parseFloat(margin.toFixed(1)) };
            });
    }, [finance?.dailyBreakdown]);

    const avgMargin = useMemo(() => {
        if (chartData.length === 0) return 0;
        const sum = chartData.reduce((acc, d) => acc + d.margin, 0);
        return parseFloat((sum / chartData.length).toFixed(1));
    }, [chartData]);

    const isEmpty = useMemo(() => chartData.length === 0, [chartData]);

    useSetChartMetadata({
        isEmpty,
        summary: !isEmpty ? (
            <span style={{ color: avgMargin >= 0 ? CHART_COLORS.revenue : CHART_COLORS.expense }}>
                {avgMargin}%
            </span>
        ) : undefined,
    });

    if (isEmpty) return null;

    return (
        <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={CHART_STYLES.margin.withAxis}>
                <defs>
                    <linearGradient id="marginGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={CHART_COLORS.profit} stopOpacity={0.3} />
                        <stop offset="100%" stopColor={CHART_COLORS.profit} stopOpacity={0.02} />
                    </linearGradient>
                </defs>
                <CartesianGrid
                    vertical={false}
                    stroke={CHART_STYLES.grid.stroke}
                    strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                    strokeWidth={CHART_STYLES.grid.strokeWidth}
                    opacity={CHART_STYLES.grid.opacity}
                />
                <XAxis
                    dataKey="date"
                    tickFormatter={(v: string) => format(parseISO(v), 'MMM d')}
                    {...CHART_STYLES.axis}
                />
                <YAxis
                    tickFormatter={(v: number) => `${v}%`}
                    {...CHART_STYLES.axis}
                />
                <Tooltip
                    contentStyle={CHART_STYLES.tooltip.contentStyle}
                    cursor={CHART_STYLES.tooltip.cursor}
                    itemStyle={CHART_STYLES.tooltip.itemStyle}
                    formatter={(value: number) => [`${value}%`, t('charts.profitMargin', 'Profit Margin')]}
                    labelFormatter={(label: string) => format(parseISO(label), 'MMM d, yyyy')}
                />
                <ReferenceLine y={0} stroke={CHART_COLORS.gray[400]} strokeDasharray="3 3" />
                <Area
                    type="monotone"
                    dataKey="margin"
                    name={t('charts.profitMargin', 'Profit Margin')}
                    stroke={CHART_COLORS.profit}
                    strokeWidth={CHART_STYLES.line.strokeWidth}
                    fill="url(#marginGradient)"
                    dot={false}
                    activeDot={CHART_STYLES.line.activeDot}
                />
            </AreaChart>
        </ResponsiveContainer>
    );
};
